import { Mic, MicOff } from "lucide-react";

/**
 * VoiceScoreGrid - Displays the score entry grid for voice mode
 * Shows hole numbers, pars, and entered scores with visual indicators
 */
const VoiceScoreGrid = ({
  voiceScores,
  numberOfHoles,
  currentVoiceHole,
  setCurrentVoiceHole,
  selectedCourseData,
  getActualHoleNumber,
  startingNine,
  isRecording,
  voiceProcessing,
  startRecording,
  stopRecording,
  voiceTranscript,
  saving,
  saveVoiceScores
}) => {
  // Calculate totals
  const front9Score = voiceScores.slice(0, Math.min(9, numberOfHoles)).reduce((sum, s) => sum + (s || 0), 0);
  const back9Score = numberOfHoles === 18 ? voiceScores.slice(9, 18).reduce((sum, s) => sum + (s || 0), 0) : 0;
  const totalScore = voiceScores.slice(0, numberOfHoles).reduce((sum, s) => sum + (s || 0), 0);
  const allFilled = voiceScores.slice(0, numberOfHoles).every(s => s !== null);

  return (
    <div className="space-y-4">
      {/* Current Hole Indicator */}
      <div className="text-center py-4 bg-stone-50 rounded-xl">
        <p className="text-sm text-stone-500 mb-1">Next Hole</p>
        <p className="text-4xl font-bold text-green-700">{getActualHoleNumber(currentVoiceHole - 1)}</p>
        <p className="text-xs text-stone-400 mt-1">
          {currentVoiceHole} of {numberOfHoles}
          {startingNine === 'back' && numberOfHoles === 18 && (
            <span className="text-amber-600 ml-1">(Back 9 first)</span>
          )}
        </p>
      </div>

      {/* Record Button */}
      <div className="flex justify-center">
        <button
          onClick={isRecording ? stopRecording : startRecording}
          disabled={voiceProcessing}
          className={`w-24 h-24 rounded-full flex flex-col items-center justify-center transition-all ${
            isRecording 
              ? 'bg-red-500 text-white animate-pulse scale-110' 
              : voiceProcessing
                ? 'bg-stone-300 text-stone-500'
                : 'bg-green-700 text-white hover:bg-green-800 hover:scale-105'
          }`}
          data-testid="voice-record-btn"
        >
          {voiceProcessing ? (
            <>
              <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin" />
              <span className="text-xs mt-1">Processing</span>
            </>
          ) : isRecording ? (
            <>
              <MicOff className="w-10 h-10" />
              <span className="text-xs mt-1">Stop</span>
            </>
          ) : (
            <>
              <Mic className="w-10 h-10" />
              <span className="text-xs mt-1">Tap to speak</span>
            </>
          )}
        </button>
      </div>

      {/* Transcript Display */}
      {voiceTranscript && (
        <div className="bg-white border border-stone-200 rounded-xl p-3">
          <p className="text-xs text-stone-500 mb-1">You said:</p>
          <p className="text-sm font-medium text-stone-800">"{voiceTranscript}"</p>
        </div>
      )}

      {/* Score Grid */}
      <div className="space-y-3">
        <div className="text-xs font-bold text-stone-500">Your Scores</div>
        
        {/* First 9 */}
        <div>
          <div className="grid grid-cols-9 gap-1">
            {voiceScores.slice(0, Math.min(9, numberOfHoles)).map((score, idx) => {
              const holePar = selectedCourseData?.hole_pars?.[idx] || null;
              const actualHole = getActualHoleNumber(idx);
              const vsParClass = score !== null && holePar !== null
                ? score < holePar ? 'ring-2 ring-red-400'
                : score > holePar ? 'ring-2 ring-blue-400'
                : ''
                : '';
              return (
                <div key={`voice-front-${idx}`} className="text-center">
                  <div className="text-xs text-stone-400 mb-1">{actualHole}</div>
                  <div 
                    onClick={() => setCurrentVoiceHole(idx + 1)}
                    className={`w-full h-10 flex items-center justify-center text-lg font-bold rounded-lg border-2 cursor-pointer ${
                      currentVoiceHole === idx + 1 
                        ? 'border-amber-500 bg-amber-50' 
                        : score !== null 
                          ? `border-green-400 bg-green-50 text-green-700 ${vsParClass}` 
                          : 'border-stone-200 bg-white text-stone-300'
                    }`}
                  >
                    {score !== null ? score : '-'}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1">
            {selectedCourseData?.hole_pars && (
              <span className="text-xs text-green-600">
                Par: {selectedCourseData.hole_pars.slice(0, Math.min(9, numberOfHoles)).reduce((sum, p) => sum + (p || 0), 0)}
              </span>
            )}
            <span className="text-sm font-bold text-stone-600">
              Out: {front9Score}
            </span>
          </div>
        </div>

        {/* Second 9 - only if 18 holes */}
        {numberOfHoles === 18 && (
          <div>
            <div className="grid grid-cols-9 gap-1">
              {voiceScores.slice(9, 18).map((score, idx) => {
                const holePar = selectedCourseData?.hole_pars?.[idx + 9] || null;
                const actualHole = getActualHoleNumber(idx + 9);
                const vsParClass = score !== null && holePar !== null
                  ? score < holePar ? 'ring-2 ring-red-400'
                  : score > holePar ? 'ring-2 ring-blue-400'
                  : ''
                  : '';
                return (
                  <div key={`voice-back-${idx}`} className="text-center">
                    <div className="text-xs text-stone-400 mb-1">{actualHole}</div>
                    <div 
                      onClick={() => setCurrentVoiceHole(idx + 10)}
                      className={`w-full h-10 flex items-center justify-center text-lg font-bold rounded-lg border-2 cursor-pointer ${
                        currentVoiceHole === idx + 10 
                          ? 'border-amber-500 bg-amber-50' 
                          : score !== null 
                            ? `border-green-400 bg-green-50 text-green-700 ${vsParClass}` 
                            : 'border-stone-200 bg-white text-stone-300'
                      }`}
                    >
                      {score !== null ? score : '-'}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-1">
              {selectedCourseData?.hole_pars && selectedCourseData.hole_pars.length > 9 && (
                <span className="text-xs text-green-600">
                  Par: {selectedCourseData.hole_pars.slice(9, 18).reduce((sum, p) => sum + (p || 0), 0)}
                </span>
              )}
              <span className="text-sm font-bold text-stone-600">
                In: {back9Score}
              </span>
            </div>
          </div>
        )}

        {/* Total and Save */}
        {selectedCourseData?.hole_pars && allFilled && (
          <div className="pt-3 border-t border-stone-200">
            {(() => {
              const totalPar = selectedCourseData.hole_pars.slice(0, numberOfHoles).reduce((sum, p) => sum + (p || 0), 0);
              const diff = totalScore - totalPar;
              return (
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-lg font-bold text-stone-800">Total: {totalScore}</span>
                    <span className={`ml-2 text-sm font-bold ${
                      diff < 0 ? 'text-red-500' : diff === 0 ? 'text-green-600' : 'text-blue-500'
                    }`}>
                      ({diff >= 0 ? '+' : ''}{diff})
                    </span>
                  </div>
                  <button
                    onClick={saveVoiceScores}
                    disabled={saving}
                    className="px-4 py-2 bg-green-700 text-white font-bold rounded-lg hover:bg-green-800 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Round'}
                  </button>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceScoreGrid;
